/**
 * Whether an item already in the `items` store needs exporting again
 * (ARCHITECTURE.md §7). Kept in its own dependency-free file so the rule is
 * unit-tested under plain Node.
 *
 * Every module used to key items on the Graph id alone, so once an item was
 * done it was never written again — an edited Teams message, a moved or
 * rescheduled event, an updated contact or a modified OneDrive file kept its
 * first-exported state forever, even when Graph's delta reported the change.
 * Each record now carries the item's `version` (a change marker Graph returns
 * with the item: lastModifiedDateTime, a content tag, …) and is re-exported
 * when the current one differs.
 *
 *  - "export" — not exported yet (or failed / skipped earlier), or changed.
 *  - "skip"   — exported, and unchanged as far as can be told.
 *  - "adopt"  — exported before versions were recorded. Assumed unchanged and
 *               stamped with the current version, so later changes are caught,
 *               rather than re-downloading every existing item once.
 */

export type ItemDecision = "export" | "skip" | "adopt";

export interface RecordedItem {
    status: string;
    version?: string;
}

export function decideItem(record: RecordedItem | undefined, version: string | undefined): ItemDecision {
    if (!record || record.status !== "done") {
        return "export";
    }
    if (version === undefined) {
        return "skip";
    }
    if (record.version === undefined) {
        return "adopt";
    }
    return record.version === version ? "skip" : "export";
}
